import { graphHooks } from '@tunasong/graph-lib/react'
import type { Entity, EntityType, Persisted } from '@tunasong/schemas'
import { getPluginByType } from '../plugin/plugin-util.js'
import { usePlugins } from './editor-plugins.js'
import { getDefaultTemplates } from './entity-templates.hook.js'

export const useCreateEntityFromTemplate = <T extends Entity = Entity>() => {
  const plugins = usePlugins('all') ?? []
  const { createEntity, createEntityStatus } = graphHooks.useEntityCreate<T>()

  const createEntityFromTemplate = async ({
    id,
    type,
    parent,
    templateName,
    optimistic,
  }: {
    // If id is provided, a temporary entity will be available immediately (unless optimistic is false)
    id?: string
    type: EntityType
    parent: Persisted<Entity> | null
    templateName?: string
    /** Pass false when the caller opens the new entity - see the cached graph's useEntityCreate */
    optimistic?: boolean
  }): Promise<Persisted<T>> => {
    // Get the template from the plugin
    const plugin = getPluginByType(type, plugins)
    if (!plugin) {
      throw new Error(`Plugin not found for type ${type}`)
    }
    const templates = plugin.templates ?? getDefaultTemplates(plugin)
    const template = templates.find(t => t.name === templateName) ?? templates[0]

    if (!template) {
      throw new Error(`Template ${name} for plugin ${type} is not found`)
    }
    // Create the entity from the template
    return createEntity({
      parent,
      entity: {
        id,
        ...template.template,
      } as T,
      optimistic,
    }) as Promise<Persisted<T>>
  }
  return { createEntityFromTemplate, createEntityStatus }
}
